
import {Component, Element, Prop, State} from '@stencil/core';
import { debounce } from "ts-debounce";

import { 
  SearchBoxSuggestionElement, 
  SearchBoxSuggestions, 
  dispatchSearchBoxSuggestionsEvent,
  SearchBoxSuggestionsBindings,
  initializeBindings, 
  Bindings} from "@coveo/atomic";
import {     
  buildCategoryFacet,
  buildCategoryFieldSuggestions,
  buildSearchParameterManager,
  CategoryFacet,
  CategoryFacetProps,
  CategoryFacetSearchResult,
  CategoryFacetValue,
  CategoryFieldSuggestions,
  SearchParameterManager,
  Unsubscribe,
  loadGenericAnalyticsActions,
  LogCustomEventActionCreatorPayload,
  CategoryFieldSuggestionsState,
  SearchBox,
  SearchEngine,
  buildSearchBox
 } from "@coveo/headless";

export interface CategorieSuggestion {
  count: number;
  displayValue: string;
  path: string[];
  rawValue: string;
}
import StarIconSVG from './svg/star.svg';
import { FieldSuggestionsValue } from '@coveo/headless';


@Component({
  tag: 'searchbox-custom-filter',
  styleUrl: 'searchbox-custom-filter.css',
  shadow: true,
})
export class SearchboxCustomFilter {
  @Element() private host!: HTMLElement;  
  @State() public error!: Error;

  @State() private isfakeSuggestion: boolean = false;

  private bindings!: Bindings;
  private suggestionBindings!: SearchBoxSuggestionsBindings<any, any>;
  private engine!: SearchEngine;
  private categoryFieldSuggestionsController!: CategoryFieldSuggestions;
  private searchBoxController!: SearchBox;
  private categoryFacetController!: CategoryFacet;
  private searchParamManagerController! : SearchParameterManager;
  private minimumQueryLength : number = 3;
  private debounceDelay : number  = 500;
  private categoryFacetUnsubscribe: Unsubscribe = () => {};
  private searchBoxUnSub: Unsubscribe = () => {};

  @State() public categorySuggestions!: CategoryFacetSearchResult[];

  private lastQueryInput : string = '-empty';

  @Prop() public label!: string;
  @Prop({reflect: false}) public facetId = 'categories';
  @Prop({reflect: false}) public field = 'ec_category';
  @Prop({reflect: false}) public basePath = 'Products';
  @Prop({reflect: false}) public delimitingCharacter = '|';
  @Prop({reflect: false}) public sortCriteria = 'alphanumeric';

  public disconnectedCallback() { 
    this.categoryFacetUnsubscribe();
    this.searchBoxUnSub();
  }

  public async connectedCallback() {
    // Wait for the Atomic to load.
    await customElements.whenDefined('atomic-search-interface');
    const sBox = document.querySelector('atomic-search-box')

    this.bindings = await initializeBindings(this.host);
    this.engine = this.bindings.engine as SearchEngine

    this.buildCategoryFacetController();
    this.buildCategoryFieldSuggestionsController();

    this.searchBoxController = buildSearchBox(this.engine)
    this.buildSearchParameterManagerController();

    if (sBox) {
      this.minimumQueryLength = sBox.minimumQueryLength;
    }

    try {
      dispatchSearchBoxSuggestionsEvent((bindings: SearchBoxSuggestionsBindings<any, any>) => {
        // controller that controls the category suggestions

      this.suggestionBindings = bindings;

      const debouncedFunction = debounce((query) => {
          this.categoryFieldSuggestionsController.updateText(query);
        }, 
        this.debounceDelay, 
        {isImmediate: false}
      );
  
      this.searchBoxUnSub = bindings.searchBoxController().subscribe(() => {
        console.log("subscribe")
        const query = bindings.searchBoxController().state.value;
        const currentState = bindings.searchBoxController().state;
  
        if (currentState.value.length >= this.minimumQueryLength && this.lastQueryInput !== query) {
          this.lastQueryInput = query;
          debouncedFunction(query)
        }
      });
      
        return this.initSubscribe();
      }, this.host);
    } 
    catch (error) {
      console.error(error);
    }
  }

  public buildCategoryFacetController () {
    const categoryFacetOptions: CategoryFacetProps = {
      options : {
        facetId: this.facetId,
        field: this.field,
        basePath: [this.basePath],
        filterByBasePath: false,
        delimitingCharacter: this.delimitingCharacter,
        sortCriteria: this.sortCriteria == 'occurrences' ? 'occurrences' : 'alphanumeric',
        numberOfValues: 10001
      }
    };

    this.categoryFacetController = buildCategoryFacet(this.engine, categoryFacetOptions);

  }

    
  private buildCategoryFieldSuggestionsController () {
    this.categoryFieldSuggestionsController  = buildCategoryFieldSuggestions(this.engine,  {
      options : {
        facet : {
          field: this.field,
          basePath: [this.basePath],
          filterByBasePath: false,
          delimitingCharacter: this.delimitingCharacter,
          sortCriteria: this.sortCriteria == 'occurrences' ? 'occurrences' : 'alphanumeric'
        }
      }
    });

    this.categoryFieldSuggestionsController.subscribe(() =>  {
      const currentState : CategoryFieldSuggestionsState = this.categoryFieldSuggestionsController.state
      if (!currentState.isLoading && currentState.query.length >= this.minimumQueryLength && currentState.values) {
        /* This allows query inputs with no query suggest/recent search or part numbers to produce instant results */
        if (currentState.values == undefined || currentState.values.length == 0 ) {
          const fakeSuggestion: CategoryFacetSearchResult = {
            displayValue: this.lastQueryInput,
            rawValue: this.lastQueryInput,
            count: 0,
            path: []
          };
          
          this.categorySuggestions = [fakeSuggestion]
          this.isfakeSuggestion = true;
        }
        else {
          this.categorySuggestions = currentState.values;
          this.isfakeSuggestion = false;
        } 
      }
    });
  }

  private buildSearchBoxController () {
    const debouncedFunction = debounce((query) => {
        this.categoryFieldSuggestionsController.updateText(query);
      }, 
      this.debounceDelay, 
      {isImmediate: false}
    );

    this.searchBoxUnSub = this.searchBoxController.subscribe(() => {
      const query = this.searchBoxController.state.value;
      const currentState = this.searchBoxController.state;

      if (currentState.value.length >= this.minimumQueryLength && this.lastQueryInput !== query) {
        this.lastQueryInput = query;
        debouncedFunction(query)
      }
    });
  }

  private buildSearchParameterManagerController () {
    this.searchParamManagerController = buildSearchParameterManager(this.engine, {
      initialState: {
        parameters : {}
      }
    }); 
  }

  private initSubscribe(): SearchBoxSuggestions {
    const titleEl = document.createElement("h4");
    titleEl.innerText = this.label;
    titleEl.classList.add("instant-title");
    titleEl.classList.add("font-bold");

    return {
      position: Array.from(this.host.parentNode!.children).indexOf(this.host),
      panel: 'left',
      renderItems: () => {
        // removes the category title when the fake search suggestion is present.
        return this.isfakeSuggestion ? 
        [
          ...this.renderItems(),
        ] :
        [
          {
            key: `category-title`,
            query: "",
            part: "instant-title",
            content: titleEl,
            hideIfLast: true,
            onSelect: () => { }
          },
          ...this.renderItems(),
        ]

      }
    };
  }

  private renderItems(): SearchBoxSuggestionElement[] {
    const query = this.suggestionBindings.searchBoxController().state.value;
    if (!query) {
      return [];
    }
    let suggestionElements: SearchBoxSuggestionElement[] = []
    if (this.categorySuggestions) {
      suggestionElements = this.categorySuggestions.map((suggestion : FieldSuggestionsValue, indexvalue: number) => {
        return this.renderItem(suggestion, indexvalue);
      });
    }

    return suggestionElements;
  }

  private renderItem(categorySuggestion: FieldSuggestionsValue, indexvalue: number): SearchBoxSuggestionElement {
    const content = document.createElement('div');
    content.className="flex items-center break-all text-left";

    if (!this.isfakeSuggestion) {
      const icon = document.createElement('img');
      icon.src = StarIconSVG;
      icon.className = "w-4 h-4 mr-2 shrink-0 hydrated"
      content.append(icon);
    }

    const span = document.createElement('span');
    span.className = "break-all line-clamp-2"

    // prevent the part number to be broken down like a category
    const valuepaths: string[] = this.isfakeSuggestion ?  [categorySuggestion.displayValue] : categorySuggestion.displayValue.split("/") 
    const displayedValue = valuepaths[valuepaths.length-1];
    const key  = 'category-' + indexvalue;

    span.innerText = displayedValue;
    content.append(span);

    const titleEl = document.createElement("h4");
    titleEl.innerText = this.label;
    titleEl.classList.add("instant-title");
    titleEl.classList.add("font-bold");

    let onSelectFunct = this.isfakeSuggestion ? () => {
      this.suggestionBindings.searchBoxController().selectSuggestion(categorySuggestion.rawValue);
    } : () => {
      // @ts-ignore
      this.select(categorySuggestion.path.concat(categorySuggestion.rawValue));
    }

    return {
      key: key,
      query: displayedValue,
      part: 'category-suggestion-item',
      content: content,
      onSelect: onSelectFunct
    };
    
  }

  private select(path: string[]) {
    const { logCustomEvent } = loadGenericAnalyticsActions (this.engine);
    const logCustomPayload : LogCustomEventActionCreatorPayload= { 
      evt: 'Custom Category',
      type: 'Category Select', 
      meta : {
        category: path
      }
    }

    this.engine.dispatch(logCustomEvent(logCustomPayload))
    this.categoryFacetController.deselectAll();
    this.categoryFacetController.toggleSelect({
        children: [],
        state: 'idle',
        numberOfResults: 0,
        value: path[path.length - 1],
        path: path,
      } as unknown as CategoryFacetValue);

    const redirectionURL = document.querySelector('atomic-search-box')?.redirectionUrl;
    // if we are not on the redirection page, redirect
    if (redirectionURL && window.location.href.indexOf(redirectionURL) == -1) {
      if (this.searchParamManagerController.state?.parameters.cf) {
        const jsonCfParams = JSON.stringify (this.searchParamManagerController.state?.parameters.cf)
        localStorage.setItem("PS_TEMP_CF_PARAMS", jsonCfParams)
        this.suggestionBindings.searchBoxController().clear()
        this.suggestionBindings.searchBoxController().submit();
      }
    }
  }
}
